import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Briefcase, Plus, Trash2, Loader2, AlertTriangle, ChevronLeft, ChevronRight, PieChart, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../lib/api';
import { formatImporte } from '../../lib/utils';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const mesLabel = (p) => { const [y, m] = (p || '').split('-'); return y ? `${MESES[+m - 1]} ${y}` : ''; };
const offset = (p, d) => { const [y, m] = p.split('-').map(Number); const t = y * 12 + m - 1 + d; return `${Math.floor(t / 12)}-${String(t % 12 + 1).padStart(2, '0')}`; };

const buildRows = (meses) => {
  const rows = [];
  [...meses].reverse().forEach(m => m.items.forEach(it => {
    let r = rows.find(x => x.concepto === it.concepto);
    if (!r) { r = { concepto: it.concepto, montos: {} }; rows.push(r); }
    r.montos[m.periodo] = it.monto;
  }));
  return rows;
};

const EstadoMes = ({ m }) => {
  if (m.aprobado) return <span className="text-[11px] text-emerald-700 flex items-center justify-end gap-1"><Lock className="w-3 h-3" />Aprobado</span>;
  if (!m.guardado && m.precargado_de) return <span className="text-[11px] text-amber-700">Sin guardar · de {mesLabel(m.precargado_de)}</span>;
  if (!m.guardado) return <span className="text-[11px] text-slate-400">Sin cargar</span>;
  return <span className="text-[11px] text-slate-500">{m.aprobadas > 0 ? `Guardado · ${m.aprobadas} aprob.` : 'Guardado'}</span>;
};

const Reparto = ({ edificioId, periodo }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/egresos/${edificioId}/gastos-negocio`, { params: { periodo } }).then(r => setD(r.data)).catch(() => setD({ reparto: [], sin_m2: [] })); }, [edificioId, periodo]);
  if (!d) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-2" data-testid="gn-reparto">
      <p className="text-slate-500">Se calcula con los m² de expensas de <b>1. Unidades</b>, solo unidades en administración (sin cocheras).</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1">
        {d.reparto.map(r => (
          <div key={r.unidad_id} className="flex justify-between"><span>{r.nombre}</span><span className="font-mono text-slate-600">{(r.pct * 100).toFixed(2)}%</span></div>
        ))}
      </div>
      {d.sin_m2.length > 0 && <p className="text-amber-700">Sin m² de expensas (no reciben gasto): {d.sin_m2.join(', ')}</p>}
    </div>
  );
};

const Planilla = ({ meses, rows, setRows, marcar }) => {
  const editable = (m) => !m.aprobado;
  const setMonto = (i, p, v) => { setRows(rows.map((r, j) => (j === i ? { ...r, montos: { ...r.montos, [p]: v } } : r))); marcar([p]); };
  const setConcepto = (i, v) => {
    setRows(rows.map((r, j) => (j === i ? { ...r, concepto: v } : r)));
    marcar(meses.filter(m => editable(m) && rows[i].montos[m.periodo] !== undefined).map(m => m.periodo));
  };
  const borrar = (i) => { marcar(meses.filter(m => editable(m) && rows[i].montos[m.periodo]).map(m => m.periodo)); setRows(rows.filter((_, j) => j !== i)); };
  const total = (p) => rows.reduce((s, r) => s + (parseFloat(r.montos[p]) || 0), 0);
  return (
    <div className="overflow-auto max-h-[55vh] rounded-lg border border-slate-200">
      <table className="w-full text-sm" data-testid="gn-planilla">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-3 py-2">Concepto</th>
            {meses.map(m => (
              <th key={m.periodo} className="text-right px-3 py-2 min-w-[150px]">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{mesLabel(m.periodo)}</div>
                <EstadoMes m={m} />
              </th>
            ))}
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-2 py-1"><Input value={r.concepto} onChange={(e) => setConcepto(i, e.target.value)} placeholder="Concepto" className="h-8" data-testid={`gn-concepto-${i}`} /></td>
              {meses.map(m => (
                <td key={m.periodo} className="px-2 py-1">
                  {editable(m)
                    ? <Input type="number" min="0" step="0.01" value={r.montos[m.periodo] ?? ''} onChange={(e) => setMonto(i, m.periodo, e.target.value)} placeholder="0" className="h-8 text-right font-mono" data-testid={`gn-monto-${i}-${m.periodo}`} />
                    : <div className="text-right font-mono text-slate-500 pr-3">{r.montos[m.periodo] ? `$ ${formatImporte(r.montos[m.periodo], 2)}` : '—'}</div>}
                </td>
              ))}
              <td className="px-1"><Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => borrar(i)} data-testid={`gn-borrar-${i}`}><Trash2 className="w-4 h-4" /></Button></td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 sticky bottom-0">
          <tr className="border-t border-slate-200">
            <td className="px-3 py-2"><Button variant="ghost" size="sm" onClick={() => setRows([...rows, { concepto: '', montos: {} }])} data-testid="gn-agregar"><Plus className="w-4 h-4 mr-1" />Agregar ítem</Button></td>
            {meses.map(m => <td key={m.periodo} className="text-right font-mono font-semibold px-3 py-2" data-testid={`gn-total-${m.periodo}`}>$ {formatImporte(total(m.periodo), 2)}</td>)}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export const GastosNegocioDialog = ({ edificioId, periodo, onDone }) => {
  const [open, setOpen] = useState(false);
  const [hasta, setHasta] = useState(periodo);
  const [meses, setMeses] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(new Set());
  const [verReparto, setVerReparto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avisos, setAvisos] = useState([]);

  const cargar = useCallback(async (h) => {
    setMeses(null);
    try {
      const r = await api.get(`/egresos/${edificioId}/gastos-negocio/historial`, { params: { hasta: h, meses: 3 } });
      setMeses(r.data.meses);
      setRows(buildRows(r.data.meses));
      const ult = r.data.meses[r.data.meses.length - 1];
      setDirty(new Set(!ult.guardado && ult.precargado_de && !ult.aprobado ? [ult.periodo] : []));
    } catch { toast.error('No se pudieron cargar los gastos de negocio'); }
  }, [edificioId]);

  useEffect(() => { if (open) cargar(hasta); }, [open, hasta, cargar]);
  const abrir = () => { setHasta(periodo); setAvisos([]); setVerReparto(false); setOpen(true); };
  const mover = (d) => {
    if (dirty.size && !window.confirm('Hay cambios sin guardar. ¿Descartarlos?')) return;
    setAvisos([]); setHasta(offset(hasta, d));
  };
  const marcar = (ps) => setDirty(new Set([...dirty, ...ps]));

  const guardar = async () => {
    setSaving(true);
    const nuevos = [];
    try {
      for (const p of [...dirty].sort()) {
        const items = rows.filter(r => r.concepto.trim() && parseFloat(r.montos[p]) > 0).map(r => ({ concepto: r.concepto.trim(), monto: parseFloat(r.montos[p]) }));
        const r = await api.put(`/egresos/${edificioId}/gastos-negocio`, { items }, { params: { periodo: p } });
        nuevos.push(...r.data.avisos.map(a => `${mesLabel(p)} · ${a}`));
      }
      toast.success('Gastos guardados y repartidos');
      setAvisos(nuevos);
      onDone?.();
      await cargar(hasta);
    } catch { toast.error('Revisá los conceptos y montos'); } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8" onClick={abrir} data-testid="gastos-negocio-btn">
        <Briefcase className="w-4 h-4 mr-1.5" /> Gastos de negocio
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="gastos-negocio-dialog">
          <DialogHeader>
            <DialogTitle>Gastos de negocio</DialogTitle>
            <DialogDescription>El mes nuevo viene con los ítems del mes anterior. Editá, agregá o borrá y guardá; se reparte entre las unidades en administración.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => mover(-1)} data-testid="gn-mes-anterior"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-slate-600 px-2" data-testid="gn-rango">{meses ? `${mesLabel(meses[0].periodo)} – ${mesLabel(meses[meses.length - 1].periodo)}` : ''}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => mover(1)} data-testid="gn-mes-siguiente"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <Button variant={verReparto ? 'default' : 'outline'} size="sm" onClick={() => setVerReparto(!verReparto)} data-testid="gn-ver-reparto">
              <PieChart className="w-4 h-4 mr-1.5" /> Ver reparto
            </Button>
          </div>
          {verReparto && <Reparto edificioId={edificioId} periodo={hasta} />}
          {!meses ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8 text-slate-400" /> : <Planilla meses={meses} rows={rows} setRows={setRows} marcar={marcar} />}
          {avisos.length > 0 && (
            <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="gn-avisos">
              {avisos.map((a, i) => <p key={i} className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 rounded px-2 py-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a}</p>)}
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            {dirty.size > 0 && <span className="text-xs text-amber-700" data-testid="gn-pendientes">Sin guardar: {[...dirty].sort().map(mesLabel).join(', ')}</span>}
            <Button onClick={guardar} disabled={!dirty.size || saving} data-testid="gn-guardar">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar y repartir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
